import React from 'react';
import { useHistory } from 'react-router-dom';
import { I18n } from '@aws-amplify/core';

import { downloadFile } from '@/utils';
import {
	ORIGINAL_STATUS_PENDING,
	ORIGINAL_STATUS_COMPLETED,
	ORIGINAL_STATUS_IN_PROGRESS
} from '@/context/DataExtraction/constants';
import { ActionButton } from './ActionButton';
import { useGetDataExtractionCSVToDownload } from '@/hooks';
import type { DataExtractionListItem } from '@/types/entities/DataExtraction';
import * as S from './styles';

interface ActionItemsProps {
	rowData: DataExtractionListItem & any;
	btnSize: 'small' | 'middle' | 'large';
	onShowDrawer: () => void;
	onDeleteActionClick: () => void;
}

export function ActionItems({ rowData, btnSize, onShowDrawer, onDeleteActionClick }: Readonly<ActionItemsProps>) {
	const history = useHistory();
	const downloadCSV = useGetDataExtractionCSVToDownload();

	const progressStatus = [ORIGINAL_STATUS_IN_PROGRESS, ORIGINAL_STATUS_PENDING];

	function handleUpdateItem(): void {
		history.push(`/settings/data-extraction/${rowData.extraction_type}/${rowData.id}`);
	}

	function handleTooltip(): string {
		return rowData.originalStatus === ORIGINAL_STATUS_COMPLETED
			? I18n.get('Download')
			: I18n.get('Download unavailable - extraction not completed');
	}

	async function handleDownloadItem(): Promise<void> {
		const data = await downloadCSV.mutateAsync({
			organization_id: rowData.organization_id,
			company_id: rowData.company_id,
			data_extraction_id: rowData.id
		});
		downloadFile(data.url);
	}

	return [
		{
			key: 1,
			icon: <S.StyledZoomInIcon />,
			label: (
				<ActionButton
					size={btnSize}
					tooltip={I18n.get('Show extraction')}
					icon={<S.StyledZoomInIcon />}
					onClick={onShowDrawer}
				/>
			)
		},
		{
			key: 2,
			icon: <S.StyledEditIcon />,
			label: (
				<ActionButton
					size={btnSize}
					tooltip={I18n.get('Edit')}
					onClick={handleUpdateItem}
					icon={<S.StyledEditIcon />}
					disabled={progressStatus.includes(rowData.originalStatus)}
				/>
			)
		},
		{
			key: 3,
			icon: <S.StyledDownloadIcon />,
			label: (
				<ActionButton
					size={btnSize}
					tooltip={handleTooltip()}
					icon={<S.StyledDownloadIcon />}
					onClick={handleDownloadItem}
					disabled={rowData.originalStatus !== ORIGINAL_STATUS_COMPLETED}
				/>
			)
		},
		{
			key: 4,
			danger: true,
			icon: <S.StyledDeleteIcon />,
			label: (
				<ActionButton
					size={btnSize}
					tooltip={I18n.get('Delete')}
					icon={<S.StyledDeleteIcon />}
					onClick={onDeleteActionClick}
				/>
			)
		}
	];
}
